import type { FeatureCollection } from 'geojson';
import { produce } from 'immer';
import type { StoreApi } from 'zustand';
import type { DataSourceConfig } from '@/components/maplibre';
import { getLayerGeoJson } from '@/app/actions/geojson';
import { MapLayerState } from '@/lib/stores/slices/mapLayers.slice';

// Layer Data Sources slice (handles both GeoJSON and Tileset data sources)
export type LayerDataSourcesState = {
  data: Record<string, DataSourceConfig>;
  isLoading: boolean;
  layerVisibility: Record<string, boolean>;
  groupVisibility: Record<string, boolean>;
  groupLayerStates: Record<string, Record<string, boolean>>;
  layerOrder: string[];
  groupOrder: string[];
};

export type LayerDataSourcesDataState = {
  dataSourcesState: LayerDataSourcesState;
  setDataSources: (dataSources: DataSourceConfig[], isLoading: boolean) => void;
  addDataSource: (dataSource: DataSourceConfig) => void;
  refreshLayerGeoJson: (layerId: string) => Promise<void>;
  removeDataSource: (layerId: string) => void;
  clearDataSources: () => void;
  toggleLayerVisibility: (layerId: string) => void;
  setLayerVisibility: (layerId: string, visible: boolean) => void;
  setGroupVisibility: (groupId: string, visible: boolean) => void;
  setAllLayersVisibility: (layerIds: string[], visible: boolean) => void;
  setAllGroupsVisibility: (groupIds: string[], visible: boolean) => void;
  initializeLayerVisibility: (visibility: Record<string, boolean>) => void;
  initializeGroupVisibility: (visibility: Record<string, boolean>) => void;
  updateLayerStyle: (layerId: string, style: any) => void;
  setLayerOrder: (order: string[]) => void;
  setGroupOrder: (order: string[]) => void;
  getGroupIdForLayer: (layerId: string) => string | null;
  preserveAndToggleGroupVisibility: (groupId: string, layerIds: string[]) => void;
} & MapLayerState;

export const createLayerDataSourcesSlice = (
  set: StoreApi<any>['setState'],
  get: StoreApi<any>['getState']
) => ({
  dataSourcesState: {
    data: {},
    isLoading: false,
    layerVisibility: {},
    groupVisibility: {},
    groupLayerStates: {},
    layerOrder: [],
    groupOrder: [],
  },
  setDataSources: (dataSources: DataSourceConfig[], isLoading: boolean) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.isLoading = isLoading;
        const newVisibility = { ...draft.dataSourcesState.layerVisibility };
        const dataRecord: Record<string, DataSourceConfig> = {};

        dataSources.forEach((ds: DataSourceConfig) => {
          dataRecord[ds.id] = ds;
          if (!(ds.id in newVisibility)) {
            newVisibility[ds.id] = true;
          }
        });

        draft.dataSourcesState.data = dataRecord;
        draft.dataSourcesState.layerVisibility = newVisibility;
      })
    ),
  addDataSource: (dataSource: DataSourceConfig) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.data[dataSource.id] = dataSource;
        if (!(dataSource.id in draft.dataSourcesState.layerVisibility)) {
          draft.dataSourcesState.layerVisibility[dataSource.id] = true;
        }
      })
    ),
  refreshLayerGeoJson: async (layerId: string) => {
    const response = await getLayerGeoJson(layerId);
    if (!response.success || !response.data) {
      console.error('Failed to refresh layer GeoJSON:', response.error);
      return;
    }

    set(
      produce((draft: LayerDataSourcesDataState) => {
        const dataSource = draft.dataSourcesState.data[layerId];
        if (dataSource && dataSource.type === 'geojson') {
          dataSource.data = response.data as FeatureCollection;
        }
      })
    );
  },
  removeDataSource: (layerId: string) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        delete draft.dataSourcesState.data[layerId];
        delete draft.dataSourcesState.layerVisibility[layerId];
      })
    ),
  setLayerOrder: (order: string[]) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.layerOrder = order;
      })
    ),
  setGroupOrder: (order: string[]) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.groupOrder = order;
      })
    ),
  clearDataSources: () =>
    set(() => ({
      dataSourcesState: {
        data: {},
        isLoading: false,
        layerVisibility: {},
        groupVisibility: {},
        groupLayerStates: {},
        layerOrder: [],
        groupOrder: [],
      },
    })),
  toggleLayerVisibility: (layerId: string) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        const currentVisibility = draft.dataSourcesState.layerVisibility[layerId] ?? true;
        draft.dataSourcesState.layerVisibility[layerId] = !currentVisibility;
      })
    ),
  setLayerVisibility: (layerId: string, visible: boolean) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.layerVisibility[layerId] = visible;
      })
    ),
  setGroupVisibility: (groupId: string, visible: boolean) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.groupVisibility[groupId] = visible;
        // GEO-713: this primitive bypasses preserveAndToggleGroupVisibility
        // (the GEO-713 auto-enable path uses it directly). Drop the now-stale
        // child-state snapshot so a later reader can't act on it. The next
        // ON->OFF via preserveAndToggleGroupVisibility will repopulate it.
        delete draft.dataSourcesState.groupLayerStates[groupId];
      })
    ),
  setAllLayersVisibility: (layerIds: string[], visible: boolean) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        const newVisibility = { ...draft.dataSourcesState.layerVisibility };
        layerIds.forEach(layerId => {
          newVisibility[layerId] = visible;
        });
        draft.dataSourcesState.layerVisibility = newVisibility;
      })
    ),
  setAllGroupsVisibility: (groupIds: string[], visible: boolean) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        const newVisibility = { ...draft.dataSourcesState.groupVisibility };
        const newLayerStates = { ...draft.dataSourcesState.groupLayerStates };
        groupIds.forEach(groupId => {
          newVisibility[groupId] = visible;
          // When forcibly hiding a group via bulk action (e.g. Clear map),
          // discard any preserved per-child snapshot so a later toggle-on
          // doesn't resurrect layers that were just cleared.
          // See preserveAndToggleGroupVisibility for the snapshot/restore flow.
          if (!visible) {
            delete newLayerStates[groupId];
          }
        });
        draft.dataSourcesState.groupVisibility = newVisibility;
        draft.dataSourcesState.groupLayerStates = newLayerStates;
      })
    ),
  initializeLayerVisibility: (visibility: Record<string, boolean>) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.layerVisibility = {
          ...draft.dataSourcesState.layerVisibility,
          ...visibility,
        };
      })
    ),
  initializeGroupVisibility: (visibility: Record<string, boolean>) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        draft.dataSourcesState.groupVisibility = {
          ...draft.dataSourcesState.groupVisibility,
          ...visibility,
        };
      })
    ),
  updateLayerStyle: (layerId: string, style: any) =>
    set(
      produce((draft: any) => {
        const dataSource = draft.dataSourcesState.data[layerId];
        if (dataSource) {
          dataSource.style = style;
        }
      })
    ),
  getGroupIdForLayer: (layerId: string) => {
    const state = get() as LayerDataSourcesDataState;
    const mapLayer = state.mapLayersList.find(ml => ml.layer_id === layerId);
    return mapLayer?.group_id || null;
  },
  preserveAndToggleGroupVisibility: (groupId: string, layerIds: string[]) =>
    set(
      produce((draft: LayerDataSourcesDataState) => {
        const currentGroupVisibility = draft.dataSourcesState.groupVisibility[groupId] ?? true;

        if (currentGroupVisibility) {
          const layerStates: Record<string, boolean> = {};
          layerIds.forEach(layerId => {
            layerStates[layerId] = draft.dataSourcesState.layerVisibility[layerId] ?? true;
          });
          draft.dataSourcesState.groupLayerStates[groupId] = layerStates;
        } else {
          const preservedStates = draft.dataSourcesState.groupLayerStates[groupId];
          if (preservedStates) {
            Object.entries(preservedStates).forEach(([layerId, visibility]) => {
              draft.dataSourcesState.layerVisibility[layerId] = visibility;
            });
          }
        }

        draft.dataSourcesState.groupVisibility[groupId] = !currentGroupVisibility;
      })
    ),
});
